import { type BottomTabBarProps } from '@react-navigation/bottom-tabs'
import { CommonActions } from '@react-navigation/native'
import { IconHeart, IconMail, IconSearch, IconSettings, IconUser } from '@tabler/icons-react-native'
import { Image } from 'expo-image'
import { getCurrentPositionAsync, LocationAccuracy, requestForegroundPermissionsAsync } from 'expo-location'
import { Tabs } from 'expo-router'
import { compact, map } from 'lodash'
import { useCallback, useEffect, useMemo, type FC } from 'react'
import { useReducedMotion } from 'react-native-reanimated'
import { graphql, useFragment, useMutation } from 'react-relay'

import { type UserNavigatorView_query$key } from '@/__generated__/UserNavigatorView_query.graphql'
import { type UserNavigatorViewUpdateProfileMutation } from '@/__generated__/UserNavigatorViewUpdateProfileMutation.graphql'
import { TabBar } from '@/components'
import { useBadgeSync, useIsMobileScreen } from '@/hooks'
import { getPublicImageUrl } from '@/lib'

// noinspection JSUnusedGlobalSymbols
export interface UserNavigatorViewProps {
  queryRef: UserNavigatorView_query$key | null | undefined
}

export const UserNavigatorView: FC<UserNavigatorViewProps> = ({ queryRef }) => {
  const isReducedMotion = useReducedMotion()
  const isMobileScreen = useIsMobileScreen()
  const queryData = useFragment(
    graphql`
      fragment UserNavigatorView_query on Query {
        currentUser {
          profile {
            photos {
              nodes {
                storageObject {
                  bucket
                  key
                }
              }
            }
            rowId
            userId
          }
          unviewedLikesCount
          unviewedMatchesCount
          unviewedMatchMessagesCount
          unviewedSystemMessagesCount
        }
        currentUserUnreadCount
      }
    `,
    queryRef
  )
  const [updateProfile] = useMutation<UserNavigatorViewUpdateProfileMutation>(graphql`
    mutation UserNavigatorViewUpdateProfileMutation($input: UpdateProfileInput!) {
      updateProfile(input: $input) {
        profile {
          geolocation {
            x
            y
          }
        }
      }
    }
  `)
  const { unviewedLikesCount, unviewedMessagesCount } = useMemo(
    () => ({
      unviewedLikesCount: queryData?.currentUser?.unviewedLikesCount ?? 0,
      unviewedMessagesCount:
        (queryData?.currentUser?.unviewedMatchMessagesCount ?? 0) +
        (queryData?.currentUser?.unviewedSystemMessagesCount ?? 0)
    }),
    [
      queryData?.currentUser?.unviewedLikesCount,
      queryData?.currentUser?.unviewedMatchMessagesCount,
      queryData?.currentUser?.unviewedSystemMessagesCount
    ]
  )

  useBadgeSync(queryData?.currentUserUnreadCount)

  useEffect(() => {
    void (async () => {
      // only run if user is logged in
      if (queryData?.currentUser?.profile?.rowId) {
        const { status } = await requestForegroundPermissionsAsync()

        if (status === 'granted') {
          try {
            const { coords } = await getCurrentPositionAsync({
              accuracy: LocationAccuracy.Lowest
            })

            if (coords) {
              updateProfile({
                variables: {
                  input: {
                    rowId: queryData.currentUser.profile.rowId,
                    patch: {
                      geolocation: {
                        x: coords.longitude,
                        y: coords.latitude
                      }
                    }
                  }
                }
              })
            }
          } catch (err) {
            console.error('[expo location] could not get location', err)
          }
        }

        // prefetch any images associated with the current user
        await Image.prefetch(
          compact(
            map(queryData.currentUser.profile.photos.nodes, (node) =>
              node?.storageObject ? getPublicImageUrl(node.storageObject.bucket, node.storageObject.key) : null
            )
          ),
          'memory-disk'
        )
      }
    })()
  }, [queryData?.currentUser?.profile?.photos?.nodes, queryData?.currentUser?.profile?.rowId, updateProfile])

  return (
    <Tabs
      backBehavior='initialRoute'
      detachInactiveScreens
      initialRouteName='search'
      screenListeners={({ route, navigation }) => ({
        blur: () => {
          const parentState = navigation.getState()
          const tabRoute = parentState.routes.find((r) => r.name === route.name)
          const nestedState = tabRoute?.state

          if (nestedState && nestedState.routes.length > 1) {
            const initialRoute = nestedState.routes[0]

            navigation.dispatch({
              ...CommonActions.reset({
                index: 0,
                routes: [{ name: initialRoute.name }]
              }),
              target: nestedState.key
            })
          }
        }
      })}
      screenOptions={{
        animation: isReducedMotion ? 'none' : 'shift',
        headerShown: false,
        lazy: true,
        popToTopOnBlur: true,
        tabBarPosition: isMobileScreen ? 'bottom' : 'left'
      }}
      tabBar={useCallback(
        (props: BottomTabBarProps) => (
          <TabBar {...props} />
        ),
        []
      )}
    >
      <Tabs.Screen
        name='search'
        options={{
          tabBarLabel: 'Search',
          tabBarIcon: ({ color, size }) => (
            <IconSearch
              color={color}
              height={size}
              strokeWidth={2.5}
              width={size}
            />
          )
        }}
      />
      <Tabs.Screen
        name='likes'
        options={{
          tabBarBadge: unviewedLikesCount,
          tabBarLabel: 'Likes',
          tabBarIcon: ({ color, size }) => (
            <IconHeart
              color={color}
              height={size}
              strokeWidth={2.5}
              width={size}
            />
          )
        }}
      />
      <Tabs.Screen
        name='messages'
        options={{
          tabBarBadge: unviewedMessagesCount,
          tabBarLabel: 'Messages',
          tabBarIcon: ({ color, size }) => (
            <IconMail
              color={color}
              height={size}
              strokeWidth={2.5}
              width={size}
            />
          )
        }}
      />
      <Tabs.Screen
        name='profile'
        options={{
          tabBarLabel: 'Profile',
          tabBarIcon: ({ color, size }) => (
            <IconUser
              color={color}
              height={size}
              strokeWidth={2.5}
              width={size}
            />
          )
        }}
      />
      <Tabs.Screen
        name='account'
        options={{
          tabBarLabel: 'Account',
          tabBarIcon: ({ color, size }) => (
            <IconSettings
              color={color}
              height={size}
              strokeWidth={2}
              width={size}
            />
          )
        }}
      />
    </Tabs>
  )
}
